import { useEffect } from 'react';
import { toast } from '@/lib/hooks/use-toast';
import { useTourStore } from '@/features/guided-tour/store/tour-store';
import { esEmpresaDemo } from './empresa-demo';
import { EVENTO_MISION_CUMPLIDA } from './aviso-de-mision';
import { MISIONES, type IdDeMision } from './misiones';
import {
  SIN_PROGRESO,
  empresaDeLaSesion,
  misionesHechas,
  useProgresoDeMisiones,
} from './progreso-de-misiones';

/*
  Mini Prixia (X3, decisión 22): una misión quedó hecha. Sólo en una demo: se tilda en
  la tarjeta, se cierra el recorrido de ESA misión si está abierto y, la primera vez,
  se celebra. Las que pasan por el servidor las anotó él (cabecera → evento →
  `anotarMisionCumplida`); las de pantalla las informa la pantalla (`avisarAccionDeMision`).
*/
function cumplir(id: IdDeMision, informarAlServidor: boolean): void {
  if (!esEmpresaDemo()) return;
  const mision = MISIONES.find((m) => m.id === id);
  if (!mision) return;
  const recien = useProgresoDeMisiones.getState().hacer(id, informarAlServidor);
  const tour = useTourStore.getState();
  if (tour.isActive && tour.currentTourId === mision.tour) {
    // Ronda Y: si su recorrido sigue con un paso de cierre, pasa a él en vez de cerrarse.
    if (mision.pasoTrasCumplir !== undefined)
      useTourStore.setState({ currentStepIndex: mision.pasoTrasCumplir });
    else tour.markCompleted(mision.tour);
  }
  if (recien) {
    // La sexta lo dice con palabras: el panel es sólo visual y la tarjeta puede estar plegada.
    const completa =
      misionesHechas(
        useProgresoDeMisiones.getState().porEmpresa[empresaDeLaSesion()] ?? SIN_PROGRESO
      ) === MISIONES.length;
    toast({
      title: '¡Misión cumplida!',
      description: completa ? 'Tu Prixia está lista' : mision.titulo,
      variant: 'success',
    });
  }
}

/** Una acción que sólo pasa en la pantalla (abrir un producto, tocar un tramo). */
export function avisarAccionDeMision(id: Extract<IdDeMision, 'monitoreo' | 'analitica'>): void {
  cumplir(id, true);
}

/** La anotó el servidor (cabecera `X-Mision-Cumplida`): sólo se refleja en la pantalla. */
export function anotarMisionCumplida(id: string): void {
  const mision = MISIONES.find((m) => m.id === id);
  if (mision) cumplir(mision.id, false);
}

/** La tarjeta de misiones escucha lo que avisa el servidor. */
export function useEscucharMisionesCumplidas(): void {
  useEffect(() => {
    const alCumplir = (e: Event) => anotarMisionCumplida(String((e as CustomEvent).detail));
    window.addEventListener(EVENTO_MISION_CUMPLIDA, alCumplir);
    return () => window.removeEventListener(EVENTO_MISION_CUMPLIDA, alCumplir);
  }, []);
}
